import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'

// Paginas publicas
import Landing from '@/pages/public/Landing'
import Plans from '@/pages/public/Plans'
import { PaymentSuccess, PaymentError } from '@/pages/public/PaymentResult'
import Privacy from '@/pages/public/Privacy'
import Terms from '@/pages/public/Terms'
import VerifyCertificate from '@/pages/public/VerifyCertificate'
import Login from '@/pages/auth/Login'
import Register from '@/pages/auth/Register'

// Paginas de estudiante - Nueva estructura
import StudentDashboard from '@/pages/student/Dashboard'
import Units from '@/pages/student/Units'
import UnitDetail from '@/pages/student/UnitDetail'
import LessonDetail from '@/pages/student/LessonDetail'
import ContentView from '@/pages/student/ContentView'
import ExerciseView from '@/pages/student/ExerciseView'
import UnitExam from '@/pages/student/UnitExam'
import FinalExam from '@/pages/student/FinalExam'
import Workshops from '@/pages/student/Workshops'
import WorkshopRoom from '@/pages/student/WorkshopRoom'
import Badges from '@/pages/student/Badges'
import RecordedWorkshops from '@/pages/student/RecordedWorkshops'

// Paginas de profesor
import TeacherDashboard from '@/pages/teacher/Dashboard'
import MyWorkshops from '@/pages/teacher/MyWorkshops'
import TeacherRecordedWorkshops from '@/pages/teacher/RecordedWorkshops'
import TeacherWorkshopMaterials from '@/pages/teacher/WorkshopMaterials'
import TeacherWorkshopPresenter from '@/pages/teacher/WorkshopPresenter'
import Students from '@/pages/teacher/Students'
import StudentDetail from '@/pages/teacher/StudentDetail'
import WorkshopAttendance from '@/pages/teacher/WorkshopAttendance'

// Paginas de gestion de contenido (profesor - solo lectura)
import ContentManagement from '@/pages/teacher/ContentManagement'
import LessonsList from '@/pages/teacher/content/LessonsList'
import ContentsList from '@/pages/teacher/content/ContentsList'
import ExercisesList from '@/pages/teacher/content/ExercisesList'
import ResourcesList from '@/pages/teacher/content/ResourcesList'

// Paginas de previsualización del curso (profesor)
import PreviewUnits from '@/pages/teacher/preview/PreviewUnits'
import PreviewUnitDetail from '@/pages/teacher/preview/PreviewUnitDetail'
import TeacherPreviewContext from '@/contexts/TeacherPreviewContext'

// Paginas de admin
import AdminDashboard from '@/pages/admin/Dashboard'
import AdminUnits from '@/pages/admin/units/UnitsList'
import AdminUnitForm from '@/pages/admin/units/UnitForm'
import AdminLessons from '@/pages/admin/lessons/LessonsList'
import AdminLessonForm from '@/pages/admin/lessons/LessonForm'
import AdminContents from '@/pages/admin/contents/ContentsList'
import AdminContentForm from '@/pages/admin/contents/ContentForm'
import AdminExercises from '@/pages/admin/exercises/ExercisesList'
import AdminExerciseForm from '@/pages/admin/exercises/ExerciseForm'
import AdminExams from '@/pages/admin/exams/ExamsList'
import AdminExamForm from '@/pages/admin/exams/ExamForm'
import AdminWorkshops from '@/pages/admin/workshops/WorkshopsList'
import AdminWorkshopForm from '@/pages/admin/workshops/WorkshopForm'
import AdminAttendanceList from '@/pages/admin/attendance/AttendanceList'
import AdminAttendanceDetail from '@/pages/admin/attendance/AttendanceDetail'
import AdminUsers from '@/pages/admin/users/UsersList'
import AdminUserEdit from '@/pages/admin/users/UserEdit'
import AdminStudentCreate from '@/pages/admin/students/StudentCreate'
import AdminTeacherCreate from '@/pages/admin/teachers/TeacherCreate'

// Paginas de admin - Niveles
import AdminLevels from '@/pages/admin/levels/LevelsList'
import AdminLevelForm from '@/pages/admin/levels/LevelForm'

// Paginas de admin - Cohortes
import AdminCohorts from '@/pages/admin/cohorts/CohortsList'
import AdminCohortForm from '@/pages/admin/cohorts/CohortForm'
import AdminCohortDetail from '@/pages/admin/cohorts/CohortDetail'

// Paginas de admin - Notas del Super Prof.
import AdminNotesList from '@/pages/admin/notes/NotesList'

// Pagina de admin - Historial de cambios in-situ
import AdminEditHistory from '@/pages/admin/edit-history/EditHistory'

// Pagina de admin - Avance de alumnos
import AdminProgressDashboard from '@/pages/admin/progress/ProgressDashboard'

// Paginas de admin - Material de talleres
import AdminWorkshopContentList from '@/pages/admin/workshop-content/WorkshopContentList'
import AdminWorkshopContentEdit from '@/pages/admin/workshop-content/WorkshopContentEdit'
import AdminWorkshopWizard from '@/pages/admin/workshop-content/WorkshopWizard'

// Paginas de admin - Talleres grabados
import AdminRecordedWorkshopsList from '@/pages/admin/recorded-workshops/RecordedWorkshopsList'
import AdminRecordedWorkshopEdit from '@/pages/admin/recorded-workshops/RecordedWorkshopEdit'

// Paginas de admin - Flujos automaticos
import AdminFlowsList from '@/pages/admin/flows/FlowsList'
import AdminFlowEditor from '@/pages/admin/flows/FlowEditor'
import AdminFlowLogs from '@/pages/admin/flows/FlowLogs'
import AdminSurveyEditor from '@/pages/admin/flows/SurveyEditor'

// Paginas de admin - Certificados
import AdminCertificateTemplates from '@/pages/admin/certificates/CertificateTemplates'
import AdminCertificateTemplateEditor from '@/pages/admin/certificates/CertificateTemplateEditor'

// Paginas de admin - Ventas
import AdminSalesDashboard from '@/pages/admin/sales/SalesDashboard'

// Paginas de admin - Email
import AdminSendEmail from '@/pages/admin/email/SendEmail'

// Paginas de profesor - Cohortes
import TeacherMyCohorts from '@/pages/teacher/MyCohorts'
import TeacherCohortManage from '@/pages/teacher/CohortManage'

// Paginas de profesor - Email
import TeacherSendEmail from '@/pages/teacher/SendEmail'

// Pagina de acceso expirado
import AccessExpired from '@/pages/student/AccessExpired'

// Componentes de layout
import MainLayout from '@/components/common/MainLayout'
import ContentLayout from '@/components/common/ContentLayout'
import TeacherLayout from '@/components/common/TeacherLayout'
import AdminLayout from '@/components/common/AdminLayout'
import LoadingScreen from '@/components/common/LoadingScreen'

// Componente de ruta publica (redirige si ya esta autenticado)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, profile } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (isAuthenticated) {
    // Redirigir segun el rol
    if (profile?.role === 'admin') {
      return <Navigate to="/admin/dashboard" replace />
    }
    if (profile?.role === 'teacher') {
      return <Navigate to="/teacher/dashboard" replace />
    }
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

// Componente de ruta protegida solo para administradores
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, profile, user } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  // Si hay usuario pero no perfil, seguir esperando
  if (user && !profile) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (profile?.role !== 'admin') {
    if (profile?.role === 'teacher') {
      return <Navigate to="/teacher/dashboard" replace />
    }
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

// Componente de ruta protegida solo para profesores
function TeacherRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, profile, user } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  // Si hay usuario pero no perfil, seguir esperando
  if (user && !profile) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (profile?.role !== 'teacher') {
    if (profile?.role === 'admin') {
      return <Navigate to="/admin/dashboard" replace />
    }
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

// Componente de ruta protegida solo para estudiantes
function StudentRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, profile, user, isAccessExpired } = useAuth()

  // Esperar mientras carga
  if (isLoading) {
    return <LoadingScreen />
  }

  // Si hay usuario pero no perfil, seguir esperando (evita redirecciones prematuras)
  if (user && !profile) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (profile?.role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />
  }

  if (profile?.role === 'teacher') {
    return <Navigate to="/teacher/dashboard" replace />
  }

  if (isAccessExpired) {
    return <AccessExpired />
  }

  return <>{children}</>
}

// Componente para manejar rutas no encontradas
function NotFoundRedirect() {
  const { isAuthenticated, isLoading, profile, user } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  // Si hay usuario pero no perfil, seguir esperando
  if (user && !profile) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />
  }

  // Redirigir al dashboard correspondiente segun el rol
  if (profile?.role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />
  }

  if (profile?.role === 'teacher') {
    return <Navigate to="/teacher/dashboard" replace />
  }

  return <Navigate to="/dashboard" replace />
}

function App() {
  return (
    <Routes>
      {/* Rutas publicas */}
      <Route path="/" element={<Landing />} />
      <Route path="/planes" element={<Plans />} />
      <Route path="/pago/exito" element={<PaymentSuccess />} />
      <Route path="/pago/error" element={<PaymentError />} />
      <Route path="/privacidad" element={<Privacy />} />
      <Route path="/terminos" element={<Terms />} />
      <Route path="/verify/:code" element={<VerifyCertificate />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route path="/register" element={<Register />} />

      {/* Rutas protegidas de estudiante */}
      <Route
        element={
          <StudentRoute>
            <MainLayout />
          </StudentRoute>
        }
      >
        <Route path="/dashboard" element={<StudentDashboard />} />

        {/* Nueva estructura de unidades */}
        <Route path="/units" element={<Units />} />
        <Route path="/unit/:unitId" element={<UnitDetail />} />
        <Route path="/lesson/:lessonId" element={<LessonDetail />} />
        <Route path="/unit/:unitId/exam" element={<UnitExam />} />
        <Route path="/final-exam" element={<FinalExam />} />

        {/* Talleres */}
        <Route path="/workshops" element={<Workshops />} />
        <Route path="/workshop/:workshopId" element={<WorkshopRoom />} />

        {/* Logros */}
        <Route path="/badges" element={<Badges />} />

        {/* Talleres grabados */}
        <Route path="/recorded-workshops" element={<RecordedWorkshops />} />
      </Route>

      {/* Contenido y ejercicios - pantalla completa sin sidebar */}
      <Route
        element={
          <StudentRoute>
            <ContentLayout />
          </StudentRoute>
        }
      >
        <Route path="/content/:contentId" element={<ContentView />} />
        <Route path="/exercise/:exerciseId" element={<ExerciseView />} />
      </Route>

      {/* Rutas protegidas de profesor */}
      <Route
        element={
          <TeacherRoute>
            <TeacherLayout />
          </TeacherRoute>
        }
      >
        <Route path="/teacher/dashboard" element={<TeacherDashboard />} />
        <Route path="/teacher/workshops" element={<MyWorkshops />} />
        <Route path="/teacher/workshop/:workshopId/attendance" element={<WorkshopAttendance />} />
        <Route path="/teacher/students" element={<Students />} />
        <Route path="/teacher/students/:profileId" element={<StudentDetail />} />

        {/* Gestion de contenido (solo lectura para profesor) */}
        <Route path="/teacher/content" element={<ContentManagement />} />
        <Route path="/teacher/content/lessons" element={<LessonsList />} />
        <Route path="/teacher/content/contents" element={<ContentsList />} />
        <Route path="/teacher/content/exercises" element={<ExercisesList />} />
        <Route path="/teacher/content/resources" element={<ResourcesList />} />

        {/* Material de talleres (presentador) */}
        <Route path="/teacher/workshop-material" element={<TeacherWorkshopMaterials />} />
        {/* Talleres grabados del profesor */}
        <Route path="/teacher/recorded-workshops" element={<TeacherRecordedWorkshops />} />

        {/* Cohortes del profesor */}
        <Route path="/teacher/cohorts" element={<TeacherMyCohorts />} />
        <Route path="/teacher/cohorts/:cohortId" element={<TeacherCohortManage />} />

        {/* Email */}
        <Route path="/teacher/email" element={<TeacherSendEmail />} />

        {/* Previsualización del curso (modo profesor) */}
        <Route path="/teacher/preview/units" element={<PreviewUnits />} />
        <Route path="/teacher/preview/unit/:unitId" element={<PreviewUnitDetail />} />
      </Route>

      {/* Previsualización - lección/contenido/ejercicio (pantalla completa, modo profesor) */}
      <Route
        element={
          <TeacherRoute>
            <TeacherPreviewContext.Provider value={{ isTeacherPreview: true }}>
              <ContentLayout />
            </TeacherPreviewContext.Provider>
          </TeacherRoute>
        }
      >
        <Route path="/teacher/preview/lesson/:lessonId" element={<LessonDetail />} />
        <Route path="/teacher/preview/content/:contentId" element={<ContentView />} />
        <Route path="/teacher/preview/exercise/:exerciseId" element={<ExerciseView />} />
      </Route>

      {/* Rutas protegidas de administrador */}
      <Route
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        <Route path="/admin/dashboard" element={<AdminDashboard />} />

        {/* CRUD de unidades */}
        <Route path="/admin/units" element={<AdminUnits />} />
        <Route path="/admin/units/new" element={<AdminUnitForm />} />
        <Route path="/admin/units/:unitId" element={<AdminUnitForm />} />
        <Route path="/admin/units/:unitId/edit" element={<AdminUnitForm />} />

        {/* CRUD de lecciones */}
        <Route path="/admin/lessons" element={<AdminLessons />} />
        <Route path="/admin/lessons/new" element={<AdminLessonForm />} />
        <Route path="/admin/lessons/:lessonId" element={<AdminLessonForm />} />
        <Route path="/admin/lessons/:lessonId/edit" element={<AdminLessonForm />} />

        {/* CRUD de contenidos */}
        <Route path="/admin/contents" element={<AdminContents />} />
        <Route path="/admin/contents/new" element={<AdminContentForm />} />
        <Route path="/admin/contents/:contentId" element={<AdminContentForm />} />
        <Route path="/admin/contents/:contentId/edit" element={<AdminContentForm />} />

        {/* CRUD de ejercicios */}
        <Route path="/admin/exercises" element={<AdminExercises />} />
        <Route path="/admin/exercises/new" element={<AdminExerciseForm />} />
        <Route path="/admin/exercises/:exerciseId" element={<AdminExerciseForm />} />
        <Route path="/admin/exercises/:exerciseId/edit" element={<AdminExerciseForm />} />

        {/* CRUD de examenes */}
        <Route path="/admin/exams" element={<AdminExams />} />
        <Route path="/admin/exams/new" element={<AdminExamForm />} />
        <Route path="/admin/exams/:examId" element={<AdminExamForm />} />
        <Route path="/admin/exams/:examId/edit" element={<AdminExamForm />} />

        {/* CRUD de talleres */}
        <Route path="/admin/workshops" element={<AdminWorkshops />} />
        <Route path="/admin/workshops/new" element={<AdminWorkshopForm />} />
        <Route path="/admin/workshops/:workshopId" element={<AdminWorkshopForm />} />
        <Route path="/admin/workshops/:workshopId/edit" element={<AdminWorkshopForm />} />

        {/* Asistencia de talleres */}
        <Route path="/admin/attendance" element={<AdminAttendanceList />} />
        <Route path="/admin/attendance/:workshopId" element={<AdminAttendanceDetail />} />

        {/* Gestion de usuarios */}
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/users/:userId/edit" element={<AdminUserEdit />} />
        <Route path="/admin/students/new" element={<AdminStudentCreate />} />
        <Route path="/admin/teachers/new" element={<AdminTeacherCreate />} />

        {/* CRUD de niveles */}
        <Route path="/admin/levels" element={<AdminLevels />} />
        <Route path="/admin/levels/new" element={<AdminLevelForm />} />
        <Route path="/admin/levels/:levelId" element={<AdminLevelForm />} />
        <Route path="/admin/levels/:levelId/edit" element={<AdminLevelForm />} />

        {/* CRUD de cohortes */}
        <Route path="/admin/cohorts" element={<AdminCohorts />} />
        <Route path="/admin/cohorts/new" element={<AdminCohortForm />} />
        <Route path="/admin/cohorts/:cohortId" element={<AdminCohortDetail />} />
        <Route path="/admin/cohorts/:cohortId/edit" element={<AdminCohortForm />} />

        {/* Notas del Super Prof. */}
        <Route path="/admin/notes" element={<AdminNotesList />} />

        {/* Historial de cambios in-situ */}
        <Route path="/admin/edit-history" element={<AdminEditHistory />} />

        {/* Avance de alumnos */}
        <Route path="/admin/progress" element={<AdminProgressDashboard />} />
        <Route path="/admin/progress/:profileId" element={<StudentDetail />} />

        {/* Material de talleres (contenido interactivo) */}
        <Route path="/admin/workshop-content" element={<AdminWorkshopContentList />} />
        <Route path="/admin/workshop-content/new" element={<AdminWorkshopWizard />} />
        <Route path="/admin/workshop-content/:contentId/edit" element={<AdminWorkshopContentEdit />} />

        {/* Talleres grabados */}
        <Route path="/admin/recorded-workshops" element={<AdminRecordedWorkshopsList />} />
        <Route path="/admin/recorded-workshops/:workshopId/edit" element={<AdminRecordedWorkshopEdit />} />

        {/* Flujos automaticos */}
        <Route path="/admin/flows" element={<AdminFlowsList />} />
        <Route path="/admin/flows/new" element={<AdminFlowEditor />} />
        <Route path="/admin/flows/:flowId/edit" element={<AdminFlowEditor />} />
        <Route path="/admin/flows/logs" element={<AdminFlowLogs />} />
        <Route path="/admin/surveys/new" element={<AdminSurveyEditor />} />
        <Route path="/admin/surveys/:surveyId/edit" element={<AdminSurveyEditor />} />

        {/* Certificados */}
        <Route path="/admin/certificates" element={<AdminCertificateTemplates />} />
        <Route path="/admin/certificates/new" element={<AdminCertificateTemplateEditor />} />
        <Route path="/admin/certificates/:templateId/edit" element={<AdminCertificateTemplateEditor />} />

        {/* Ventas */}
        <Route path="/admin/sales" element={<AdminSalesDashboard />} />

        {/* Email */}
        <Route path="/admin/email" element={<AdminSendEmail />} />
      </Route>

      {/* Presentador de talleres - pantalla completa sin layout */}
      <Route path="/teacher/workshop-material/:contentId/present" element={<TeacherRoute><TeacherWorkshopPresenter /></TeacherRoute>} />
      <Route path="/admin/workshop-content/:contentId/present" element={<AdminRoute><TeacherWorkshopPresenter /></AdminRoute>} />

      {/* Ruta 404 - redirige segun el estado de autenticacion */}
      <Route path="*" element={<NotFoundRedirect />} />
    </Routes>
  )
}

export default App
